<template>
  <slot
    :set-ref="setActivatorRef"
    name="activator"
  />

  <div
    ref="contentRef"
    popover="manual"
    :class="popoverClasses"
    :style="contentStyles"
    data-cy="ds-popover__content"
  >
    <span
      v-if="props.withArrow"
      :class="DS_POPOVER_CLASSES.Arrow"
      aria-hidden="true"
      data-cy="ds-popover__arrow"
    />
    <slot
      v-if="modelValue"
      name="content"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { onClickOutside, onKeyStroke } from "@vueuse/core";
import { DS_POPOVER_PLACEMENTS, DSPopoverProps } from "../types/props";
import { DS_POPOVER_ARROW_SIDE_CLASS, DS_POPOVER_CLASSES } from "../constants/classes";
import { DS_POPOVER_PLACEMENT_TO_ARROW_SIDE } from "../types/arrow";
import { usePopoverStyles } from "../composables/usePopoverStyles";
import { useScrollHandlers } from "../composables/useScrollHandlers";
import {
  markPopoverOpen,
  removeOpenPopover,
  shouldHandlePopoverEscape,
  type OpenPopoverEntry,
} from "../utils/openPopoverStack";

const props = withDefaults(defineProps<DSPopoverProps>(), {
  offset: 6,
  placement: DS_POPOVER_PLACEMENTS.BottomStart,
  inheritActivatorWidth: false,
  activatorElement: undefined,
  closeOnClickOutside: true,
  constrainToViewport: true,
  withArrow: false,
});

const modelValue = defineModel<boolean>({ default: false });
const emit = defineEmits<{
  "open:change": [open: boolean];
  escape: [];
  /** The content was (re)positioned against the activator. */
  positioned: [];
}>();

const popoverClasses = computed(() => [
  DS_POPOVER_CLASSES.Popover,
  props.withArrow && [
    DS_POPOVER_CLASSES.WithArrow,
    DS_POPOVER_ARROW_SIDE_CLASS[DS_POPOVER_PLACEMENT_TO_ARROW_SIDE[props.placement]],
  ],
]);

const activatorRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();
const openPopoverEntry: OpenPopoverEntry = {
  id: Symbol("ds-popover"),
  contains: target => Boolean(contentRef.value?.contains(target) || activatorRef.value?.contains(target)),
  element: () => contentRef.value,
};

const setActivatorRef = (el: HTMLElement | null) => {
  if (props.activatorElement) return;
  activatorRef.value = el || undefined;
};

watch(
  () => props.activatorElement,
  element => {
    if (element) activatorRef.value = element;
  },
  { immediate: true }
);
const { contentStyles, calculateStyles, setVisibility } = usePopoverStyles({
  activatorRef,
  contentRef,
  placement: () => props.placement,
  offset: () => props.offset,
  inheritActivatorWidth: props.inheritActivatorWidth,
  constrainToViewport: () => props.constrainToViewport,
  withArrow: () => props.withArrow,
  onPositioned: () => emit("positioned"),
});

const { setupScrollHandlers, cleanupScrollHandlers } = useScrollHandlers({
  activatorRef,
  onScroll: calculateStyles,
});

const openPopover = (activator?: HTMLElement) => {
  if (activator) setActivatorRef(activator);
  modelValue.value = true;
  return true;
};

const closePopover = () => {
  removeOpenPopover(openPopoverEntry);
  modelValue.value = false;
  return false;
};

const togglePopover = (activator?: HTMLElement) => {
  if (!modelValue.value && activator) setActivatorRef(activator);
  modelValue.value = !modelValue.value;
  return modelValue.value;
};

const showPopover = async () => {
  if (!contentRef.value) return;

  markPopoverOpen(openPopoverEntry);
  try {
    contentRef.value.showPopover();
  } catch {
    // Already open — no-op
  }
  setVisibility("hidden");
  await nextTick();
  setupScrollHandlers();
  await calculateStyles();
  setVisibility("visible");
};

const hidePopover = () => {
  if (!contentRef.value) return;

  removeOpenPopover(openPopoverEntry);
  try {
    contentRef.value.hidePopover();
  } catch {
    // Already closed — no-op
  }
  setVisibility("hidden");
  cleanupScrollHandlers();
};

watch(modelValue, isOpen => {
  emit("open:change", isOpen);
  if (isOpen) void showPopover();
  else hidePopover();
});

onMounted(() => {
  if (modelValue.value) {
    emit("open:change", true);
    void showPopover();
  }
});

onKeyStroke("Escape", event => {
  if (!modelValue.value) return;

  const eventTarget = event.target instanceof Node ? event.target : null;
  const activeElement = document.activeElement;
  if (!shouldHandlePopoverEscape(openPopoverEntry, event, [eventTarget, activeElement])) return;

  event.preventDefault();
  emit("escape");
  closePopover();
});

onClickOutside(
  contentRef,
  () => {
    if (modelValue.value && props.closeOnClickOutside) {
      closePopover();
    }
  },
  { ignore: [activatorRef] }
);

onUnmounted(() => {
  removeOpenPopover(openPopoverEntry);
  cleanupScrollHandlers();
});

defineExpose({
  open: openPopover,
  close: closePopover,
  toggle: togglePopover,
});
</script>
